import { getDate } from "../../../utils/date/date";
import { DateHelper } from "../DateHelper/DateHelper";

export class DRE {
    static RECEITA_BRUTA = "rbc33bB8KFBNznXsX";
    static RECEITA_LIQUIDA = "ABk7k5w7AARjAcTsi";
    constructor({
        Serie,
        CalculatingMachine,
        Point,
        CalculatingMachineV2Gateway
    } = {}) {
        this.Serie = Serie;
        this.Point = Point;
        this.CalculatingMachine = CalculatingMachine;
        this.CalculatingMachineV2Gateway = CalculatingMachineV2Gateway;
    }

    getPoint({ points, time, serie, opt, scenarioId = "ORIGINAL" }) {
        const point = points.find((point) => {
            return (
                point.s === serie._id &&
                point.t.getTime() === getDate(time).getTime() &&
                point.sc === scenarioId
            );
        });

        return this.CalculatingMachine.valueGet(serie, point, null, opt);
    }

    getPointGoal({ points, serie, opt }) {
        const point = points.find((point) => {
            return point.s === serie._id;
        });

        return this.CalculatingMachine.valueGet(serie, point, null, opt);
    }

    getSeriesAndPointsReportFin({
        companyId,
        period,
        source,
        scenarioId = "ORIGINAL",
        options = {},
        verticalReference = false
    }) {
        let series = [];
        series = this.Serie.get(
            {
                companyId: "EMPRESA_NUCONT",
                sources: "fin"
            },
            {
                $referenceAssociated: true,
                sort: { classifier: 1 }
            },
            companyId
        );

        if (verticalReference && source === "fin") {
            const revenues = {
                "RECEITA LIQUIDA": "Receita Líquida",
                "RECEITAS OPERACIONAIS": "Receitas Operacionais"
            };
            const verticalReferenceSerie = this.Serie.findOneCollection({
                name: revenues[verticalReference]
            });
            series.push(verticalReferenceSerie);
        }

        this.CalculatingMachine.setSource(source);

        const pointsResult = this.CalculatingMachine.result({
            companyId: companyId,
            serieIds: series.map((serie) => serie._id),
            scenario: scenarioId,
            period: period,
            options: {
                recalculate: true,
                visualizationAccumulated: false,
                ...options
            }
        });

        return { series, points: pointsResult };
    }

    /**
     * @param {String} groupId Presente quando a DRE de grupo esta com a visao por empresa ligada.
     *
     * `Serie.populateGroup` guarda as series das empresas-membro com o `companyId` DELAS e marca
     * todas com `groupId`. Filtrar por `companyId` do grupo, como aqui sempre se fez, encontrava
     * apenas as raizes consolidadas -- as series das empresas ficavam na colecao e fora da tabela.
     * Com o `groupId` no seletor, grupo e membros vem juntos.
     */
    getSeriesAndPoints({
        companyId,
        period,
        source,
        scenarioId = "ORIGINAL",
        options = {},
        groupId = null
    }) {
        let series = [];

        if (source == "fin") {
            series = this.Serie.get(
                groupId
                    ? { groupId, sources: "fin" }
                    : {
                          companyId,
                          sources: "fin"
                      },
                {
                    $referenceAssociated: true,
                    sort: { classifier: 1 }
                },
                companyId
            );
        } else {
            series = this.Serie.get(
                {
                    sources: source,
                    companyId: "EMPRESA_NUCONT",
                    "metadata.origin": "dre"
                },
                {
                    $referenceAssociated: true,
                    sort: { classifier: 1 }
                },
                companyId
            );
        }

        this.CalculatingMachine.setSource(source);

        const pointsResult = this.CalculatingMachine.result({
            companyId: companyId,
            serieIds: series.map((serie) => serie._id),
            scenario: scenarioId,
            period: period,
            options: {
                recalculate: true,
                visualizationAccumulated: false,
                ...options
            }
        });

        return { series, points: pointsResult };
    }

    /**
     * @public
     * Get the year
     * @param {DateString} actualDate Actual date
     * @returns {Number} Year
     */
    onYearGet = (actualDate) => {
        if (actualDate) {
            const t = new DateHelper(actualDate)
                .startOf("month")
                .month(0)
                .format("YYYY");

            return parseInt(t);
        } else {
            return new Date().getUTCFullYear();
        }
    };

    async populate({
        companyId,
        years,
        source,
        isGroup,
        companyIds,
        companies = []
    }) {
        if (source == "fin") {
            // No grupo, carregar só o `groupId` traz apenas as ~11 raízes consolidadas: o
            // `compound` delas aponta para séries das empresas-membro, que ficavam de fora, e sem
            // esses filhos em memória a DRE não tem o que expandir. `populateGroup` já busca
            // `[...companyIds, groupId]` numa consulta só e desambigua o nome por empresa.
            if (isGroup && companyIds?.length) {
                return await this.Serie.populateGroup({
                    companyIds,
                    groupId: companyId,
                    source,
                    companies,
                    isDre: true
                });
            }
            return await this.Serie.populateDepartament({
                companyId,
                source,
                origin: "dre"
            });
        } else {
            return await Promise.all([
                this.Serie.getByCompanyDemand({
                    companyId: companyIds ? companyIds : companyId,
                    seriesIds: [
                        "9992k1z7AARjAcTsi",
                        "ABk7k5w7AARjAcTsi",
                        "B5uExTX6KGf2KFBWQ",
                        "C6uExTX6KGf2KFBWQ",
                        "D7uExTX6KGf2KFBWQ",
                        "E8uExTX6KGf2KFBWQ",
                        "F9uExTX6KGf2KFBWQ",
                        "G1uExTX6KGf2KFBWQ",
                        "GLk2k1z7AARjAcTsi",
                        "H2uExTX6KGf2KFBWQ",
                        "I3uExTX6KGf2KFBWQ",
                        "JDE2eqh9F4mwoeBJXh",
                        "PQk2k1z7AARjAcTsi",
                        "PiR2k1z7AVEjAcAsi",
                        "ZLk2k1z7AARjAcTsi",
                        "miuExTX6KGf2KFBWQ",
                        "rbc33bB8KFBNznXsX",
                        "Qfmph573HMafAhrB3"
                    ]
                })
            ]);
        }
    }
    async populateCalc({
        scenarioId,
        periodName,
        company,
        source,
        years,
        currentDate,
        currentComparation,
        allYearsAvailable,
        projection,
        budget,
        allMonthsByYear,
        seriesIds
    }) {
        await this.CalculatingMachineV2Gateway.calculateSeries({
            companyData: company,
            source,
            scenarioId,
            analysisPeriod: periodName,
            years,
            currentDate,
            currentComparation,
            allYearsAvailable,
            projection,
            budget,
            allMonthsByYear,
            seriesIds
        });
    }
}
